import { useEffect, useState } from "react";
import { MessageSquare, Save, Plus, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { updateConfig } from "@/lib/api";
import { DEFAULT_WHATSAPP_TEMPLATE, DEFAULT_MOTIVOS_PADRAO, renderWhatsAppTemplate } from "@/lib/plans";

const PLACEHOLDERS = [
  { key: "{primeiroNome}", desc: "Primeiro nome do prospecto" },
  { key: "{nome}", desc: "Nome completo do prospecto" },
  { key: "{vendedor}", desc: "Nome do vendedor" },
  { key: "{motivo}", desc: "Motivo original de não-viabilidade" },
  { key: "{motivoParenteses}", desc: "Motivo entre parênteses (ou vazio)" },
  { key: "{localidade}", desc: "Bairro/endereço do prospecto" },
];

const PREVIEW_SAMPLE = {
  nome: "Restaurante Alto",
  vendedor: "Ana Souza",
  motivo: "Fora da área de cobertura",
  localidade: "Vila Madalena",
};

export default function ComunicacaoCard({ config, onChange }) {
  const [template, setTemplate] = useState(config?.whatsapp_template || DEFAULT_WHATSAPP_TEMPLATE);
  const [motivos, setMotivos] = useState(config?.motivos_padrao?.length ? config.motivos_padrao : DEFAULT_MOTIVOS_PADRAO);
  const [novoMotivo, setNovoMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTemplate(config?.whatsapp_template || DEFAULT_WHATSAPP_TEMPLATE);
    setMotivos(config?.motivos_padrao?.length ? config.motivos_padrao : DEFAULT_MOTIVOS_PADRAO);
  }, [config]);

  const preview = renderWhatsAppTemplate(template, PREVIEW_SAMPLE);

  const addMotivo = () => {
    const v = (novoMotivo || "").trim();
    if (!v) return;
    if (motivos.includes(v)) { toast.error("Motivo já existe"); return; }
    setMotivos((prev) => [...prev, v]);
    setNovoMotivo("");
  };
  const removeMotivo = (idx) => setMotivos((prev) => prev.filter((_, i) => i !== idx));
  const editMotivo = (idx, val) => setMotivos((prev) => prev.map((m, i) => (i === idx ? val : m)));

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await updateConfig({
        plans: config.plans || [],
        bonus_pct: config.bonus_pct ?? 0.05,
        team_goal: config.team_goal ?? 0,
        team_bonus_pct: config.team_bonus_pct ?? 0.03,
        whatsapp_template: template.trim() || DEFAULT_WHATSAPP_TEMPLATE,
        motivos_padrao: motivos.map((m) => m.trim()).filter(Boolean),
      });
      onChange && onChange(saved);
      toast.success("Comunicação atualizada");
    } catch { toast.error("Erro ao salvar comunicação"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="comunicacao-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Comunicação & padrões</h3>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="btn-save-comunicacao">
          <Save className="w-4 h-4 mr-1.5" /> Salvar
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Template WhatsApp */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Mensagem padrão do WhatsApp</Label>
          <Textarea
            rows={5}
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            className="mt-2"
            data-testid="input-whatsapp-template"
          />
          <div className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="w-3 h-3 mt-0.5" />
            <div className="flex flex-wrap gap-1">
              {PLACEHOLDERS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setTemplate((prev) => prev + " " + p.key)}
                  className="px-1.5 py-0.5 rounded border border-border text-[10px] hover:bg-primary/10 hover:text-primary"
                  title={p.desc}
                >
                  {p.key}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3 rounded-lg border border-border bg-background p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Prévia</div>
            <p className="text-sm text-foreground whitespace-pre-wrap">{preview}</p>
          </div>
        </div>

        {/* Motivos padrão */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Motivos padronizados de não-viabilidade</Label>
          <div className="mt-2 space-y-2">
            {motivos.map((m, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`motivo-row-${idx}`}>
                <Input value={m} onChange={(e) => editMotivo(idx, e.target.value)} />
                <Button size="icon" variant="ghost" onClick={() => removeMotivo(idx)} data-testid={`btn-remove-motivo-${idx}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Input
              placeholder="Adicionar novo motivo…"
              value={novoMotivo}
              onChange={(e) => setNovoMotivo(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMotivo())}
              data-testid="input-novo-motivo"
            />
            <Button variant="outline" onClick={addMotivo} data-testid="btn-add-motivo">
              <Plus className="w-4 h-4 mr-1.5" /> Adicionar
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-1">
            {motivos.slice(0, 8).map((m) => (
              <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
